import React, { useMemo } from 'react'
import { useParamsInt } from '../../../../../shared/hooks/location'
import { Route, useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min'
import { BreadcrumbView } from '../../../../../context/breadcrumb.context'
import { IconCategory, IconLibrary } from '../../../../../shared/icon/icon'
import { CardFullHeight } from '../../../../../shared/card/card-full-height'
import { NodeLibraryGridItem } from './node-library-grid-item'
import { NodeLibraryAdd } from './pages/node-library-add'
import { NodeLibraryDetails } from './pages/node-library-details'
import { NodeCategory } from '../node-category/node-category'
import {
  useApiFormNodeLibraryCategory,
  useApiListNodeDecendantLibrary,
  useApiListNodeLibraryCategory
} from '../../../../../api-new/state/content'
import { NodeCategoryGridItem } from '../node-category/node-category-grid-item'
import { NodeSearchArea } from '../common/search/node-search-area'
import { LIBRARY_SEARCH } from '../common/search/search-areas'
import { buildLibraryCategoryPath } from '../common/shared/build-path'
import { DataProvider } from './data-context'
import { useDataContext } from './data-context'
import { NestedNodeAddress } from '../common/shared/nested-path'
import { folderNotFound } from '../common/shared/node-not-found'
import { notFoundText } from '../../../../../shared/page/record-view'
import { FOLDER_SECTION, FolderMenu } from '../common/menu/node-actions'
import { NodeGridBody } from '../common/search/node-grid-body'
import { SECTION } from '../../../../../shared/navigation/sections'

export const NodeLibraryGrid = () => {
  return (
    <DataProvider>
      {/* An item or folder opened by its id alone goes under its folders. */}
      <NestedNodeAddress useData={useDataContext} />
      <div className="container-fluid p-0 h-100">
        <NodeLibraryGridBranch />
      </div>
    </DataProvider>
  )
}

const NodeLibraryGridBranch = ({ grandParentURL }) => {
  const { url } = useRouteMatch()
  const { parentID } = useParamsInt()

  const dataContext = useDataContext()

  const category = useMemo(() => dataContext.categories.find(i => i.id === parentID), [dataContext, parentID])

  const categories = useMemo(
    () => {
      if (parentID) return dataContext.categories.filter(i => i.parentID === parentID)
      else return dataContext.categories.filter(i => i.parentID === null)
    },
    [dataContext, parentID]
  )

  const libraries = useMemo(
    () => {
      if (parentID) return dataContext.libraries.filter(i => i.parentID === parentID)
      else return dataContext.libraries.filter(i => i.parentID === null)
    },
    [dataContext, parentID]
  )

  // The section's top is its sidebar entry, its name and its icon
  // (shared/navigation/sections.js); a folder is named after itself, and
  // nothing while its name loads.
  const folderMissing = !!parentID && dataContext.loaded && !dataContext.error && !category
  const title = useMemo(() => (parentID ? (category ? category.name : folderMissing ? notFoundText('folder') : '') : SECTION.assetLibrary.text), [category, parentID, folderMissing])

  const Icon = useMemo(
    () => {
      if (parentID) return IconCategory
      else return SECTION.assetLibrary.Icon
    },
    [parentID]
  )

  // The section's or the folder's own menu, as on its card.
  const HeaderMenu = () => <FolderMenu place="header" url={url} nodeID={parentID} section={FOLDER_SECTION.LIBRARY} />

  // Zero state. The library's header menu has no add action for the viewer
  // to reuse, so this one explains and offers none.
  const isEmpty = categories.length === 0 && libraries.length === 0

  const emptyState = folderMissing
    ? folderNotFound(SECTION.assetLibrary)
    : {
        Icon: parentID ? IconCategory : IconLibrary,
        title: parentID ? 'This category is empty' : 'No library items yet',
        message: parentID ? 'Library items added to this category appear here.' : 'Shared assets appear here once they are added to your asset library.'
      }

  return (
    <React.Fragment>
      <BreadcrumbView Icon={Icon} text={title}>
        <CardFullHeight title={title} HeaderMenu={folderMissing ? undefined : HeaderMenu}>
          <NodeSearchArea
            area={LIBRARY_SEARCH}
            nodes={dataContext.items}
            thumbnails={dataContext.thumbnails}
            items={dataContext.items}
            loaded={dataContext.loaded}
            error={dataContext.error}
            onRetry={dataContext.retry}>
            <NodeGridBody loaded={dataContext.loaded} isEmpty={isEmpty} emptyState={emptyState}>
              <div className="container-fluid">
                <div className="node-grid">
                  {categories
                    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                    .map((node, i) => <NodeCategoryGridItem key={i} node={node} dataContext={dataContext} section={FOLDER_SECTION.LIBRARY} />)}

                  {libraries
                    .sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1))
                    .map((node, i) => <NodeLibraryGridItem key={i} node={node} dataContext={dataContext} />)}
                </div>
              </div>
            </NodeGridBody>
          </NodeSearchArea>
        </CardFullHeight>
      </BreadcrumbView>

      <Route path={`${url}/node/:parentID`}>
        <NodeLibraryGridBranch grandParentURL={url} />
      </Route>

      <Route path={`${url}/library/:nodeID`}>
        <NodeLibraryDetails parentURL={url} />
      </Route>

      <Route path={`${url}/add-type`}>
        <NodeLibraryAdd parentURL={url} parentID={parentID} />
      </Route>

      <NodeCategory
        url={url}
        grandParentURL={grandParentURL}
        nodeID={parentID}
        rootName="LIBRARY"
        useApiListCategory={useApiListNodeLibraryCategory}
        useApiFormCategory={useApiFormNodeLibraryCategory}
        useApiListRecycle={useApiListNodeDecendantLibrary}
        buildPathFn={buildLibraryCategoryPath}
        dataContext={dataContext}
        section={FOLDER_SECTION.LIBRARY}
      />
    </React.Fragment>
  )
}
